/**
 * Cliente HTTP de compras (loja-api).
 */

const API_BASE_URL =
  (typeof window !== 'undefined' && window.__REACT_APP_LOJA_API_URL) ||
  process.env.REACT_APP_LOJA_API_URL ||
  'http://localhost:4001';

async function request(path, { method = 'GET', body, token } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const url = `${API_BASE_URL}${path}`;
  console.log(`🔗 [purchaseService] ${method} ${url}`, body ? { body } : '');

  const response = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const data = await response.json().catch(() => ({}));
  
  console.log(`📊 [purchaseService] Response (${response.status}):`, data);
  
  if (!response.ok) {
    const err = new Error(data?.error || `Erro HTTP ${response.status}`);
    err.status = response.status;
    throw err;
  }
  return data;
}

export const purchaseService = {
  /**
   * @param {string} token
   * @param {{ items: Array<{type:'ebook'|'course', itemId:string, title:string, valor:number}>,
   *          paymentMethod: 'pix'|'credit_card'|'free',
   *          txid?: string,
   *          chargeId?: string,
   *          email?: string,
   *          cpf?: string,
   *          name?: string }} payload
   */
  record: (token, payload) =>
    request('/api/purchases/record', { method: 'POST', token, body: payload }),

  /**
   * Registra compra de usuário ANÔNIMO (sem autenticação).
   * Será reivindicada via claimOrphanPurchases quando o usuário fizer login.
   * @param {{ items: Array<{type:'ebook'|'course', itemId:string, title:string, valor:number}>,
   *          paymentMethod: 'pix'|'credit_card'|'free',
   *          txid?: string,
   *          chargeId?: string,
   *          email: string,
   *          cpf: string,
   *          name: string }} payload
   */
  recordAnonymous: (payload) =>
    request('/api/purchases/record-anonymous', { method: 'POST', body: payload }),

  listMine: (token) => request('/api/purchases/mine', { token }),

  /**
   * Busca e associa compras órfãs (anônimas) ao usuário autenticado.
   * @param {string} token - Token JWT do usuário autenticado
   * @param {string} email - Email para buscar compras órfãs
   * @returns {Promise<{claimed: number, purchases: Array}>}
   */
  claimOrphanPurchases: (token, email, cpf) =>
    request('/api/purchases/claim-orphan', {
      method: 'POST',
      token,
      body: { email, ...(cpf ? { cpf } : {}) },
    }),

  /**
   * Sincroniza compras anônimas (localStorage) para a conta do usuário após login.
   * @param {string} token - Token JWT do usuário autenticado
   * @param {string} userEmail - Email do usuário autenticado (para identificar compras)
   * @returns {Promise<{synced: number, failed: number}>}
   */
  async syncAnonymousPurchasesToAccount(token, userEmail) {
    try {
      console.log('🔄 [purchaseService] Iniciando sincronização de compras anônimas...');
      console.log('   Email do usuário:', userEmail);
      
      // 1. Buscar compras anônimas do localStorage
      const STORAGE_KEY = 'purchasedEbooks';
      const stored = localStorage.getItem(STORAGE_KEY);
      
      console.log('📂 [purchaseService] localStorage.getItem("' + STORAGE_KEY + '"):', stored);
      
      const anonymousPurchases = stored ? JSON.parse(stored) : [];

      if (!Array.isArray(anonymousPurchases) || anonymousPurchases.length === 0) {
        console.log('ℹ️ [purchaseService] Nenhuma compra anônima para sincronizar');
        return { synced: 0, failed: 0 };
      }

      console.log(`📦 [purchaseService] Encontradas ${anonymousPurchases.length} compra(s) anônima(s) no localStorage`);
      console.log('   Detalhes:', anonymousPurchases);

      // 2. Converter para formato da API
      // Agrupar por (paymentMethod + transactionId) para garantir que cada
      // transação seja verificada com seu próprio ID — evita validar txid de
      // outra compra em nome do ebook completo (que tem txid diferente).
      const groups = {};

      anonymousPurchases.forEach((p) => {
        if (!p.ebookId) return;

        const item = {
          type: 'ebook',
          itemId: p.ebookId,
          title: p.titulo || p.title || 'eBook',
          valor: p.preco || 0,
        };

        const originalMethod = p.paymentMethod || 'unknown';
        const hasPrice = item.valor > 0;
        const hasTransactionId = p.transactionId && p.transactionId !== 'unknown';

        let paymentMethod = 'free';
        if (hasPrice && hasTransactionId && (originalMethod === 'pix' || originalMethod === 'credit_card')) {
          paymentMethod = originalMethod;
        }

        // Chave: método + transactionId — cada transação gera seu próprio lote
        const txKey = paymentMethod === 'free' ? 'free' : `${paymentMethod}:${p.transactionId}`;
        if (!groups[txKey]) {
          groups[txKey] = { paymentMethod, transactionId: p.transactionId, items: [] };
        }
        groups[txKey].items.push(item);
      });

      console.log('📊 [purchaseService] Grupos de sincronização:',
        Object.entries(groups).map(([key, g]) => ({
          key,
          count: g.items.length,
        }))
      );

      // 3. Enviar para a API um lote por transação
      let totalSynced = 0;
      let totalFailed = 0;

      for (const { paymentMethod, transactionId, items } of Object.values(groups)) {
        const payload = { items, paymentMethod };
        if (paymentMethod === 'pix') payload.txid = transactionId;
        else if (paymentMethod === 'credit_card') payload.chargeId = transactionId;

        console.log(`📤 [purchaseService] Enviando ${items.length} compra(s) com método '${paymentMethod}'...`);
        console.log('   Items:', items.map((i) => i.itemId));

        try {
          const response = await this.record(token, payload);
          console.log(`✅ [purchaseService] ${items.length} compra(s) '${paymentMethod}' sincronizadas:`, response);
          totalSynced += items.length;
        } catch (err) {
          console.error(`❌ [purchaseService] Falha ao sincronizar ${items.length} compra(s) '${paymentMethod}':`, err.message);
          totalFailed += items.length;
        }
      }

      // 4. Se TODAS foram sincronizadas com sucesso, limpar localStorage
      if (totalFailed === 0 && totalSynced > 0) {
        localStorage.removeItem(STORAGE_KEY);
        console.log('🗑️ [purchaseService] localStorage de compras anônimas limpo');
      } else if (totalFailed > 0) {
        console.warn(`⚠️ [purchaseService] ${totalFailed} compra(s) não sincronizadas - localStorage preservado para tentar depois`);
      }

      return { synced: totalSynced, failed: totalFailed };
    } catch (err) {
      console.error('❌ [purchaseService] Erro ao processar sincronização:', err);
      return { synced: 0, failed: 1 };
    }
  },
};
